import axios, { AxiosInstance } from 'axios'
import { getInstance, getInstanceAsync } from '@/auth'
import { SnakeCasedPropertiesDeep } from 'type-fest'
import { setupETagInterceptor } from '@/api/etagInterceptor'
// Will only add the bearer token to endpoints that start with the base
export const BASE_API_ENDPOINT = '/api'

const addAuthInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    async function (config) {
      const authService = getInstance()

      if (!authService) {
        return config
      }

      const readyAuthService = authService.loading
        ? await getInstanceAsync().catch(() => null)
        : authService

      if (!readyAuthService) {
        return config
      }

      const token = await readyAuthService.getTokenSilently()

      if (token && config.url?.startsWith(BASE_API_ENDPOINT)) {
        if (!config.headers) {
          config.headers = {} as typeof config.headers
        }
        Object.assign(config.headers, { Authorization: `Bearer ${token}` })
      }

      return config
    },
    function (error) {
      return Promise.reject(error)
    },
  )

  return instance
}

export const defaultVersion = '2024-06-01'

export function createClient(version: string) {
  const instance: AxiosInstance = addAuthInterceptor(axios.create())
  setupETagInterceptor(instance)
  instance.defaults.headers.common['Api-Version'] = version

  return instance
}

export const ironsightAxios = createClient(defaultVersion)

export function get<T = any>(url: string, args = {}) {
  return ironsightAxios.get<SnakeCasedPropertiesDeep<T>>(url, args)
}

export function patch(url: string, args: any = {}) {
  return ironsightAxios.patch(url, args.data || {}, args.config || {})
}

export function post(url: string, args: any = {}) {
  return ironsightAxios.post(url, args.data || {}, args.config || {})
}

export function put(url: string, args: any = {}) {
  return ironsightAxios.put(url, args.data || {}, args.config || {})
}

export function del(url: string, args: any = {}) {
  return ironsightAxios.delete(url, args || {})
}
